import { useLayoutEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  CHART_BASELINE_Y,
  CHART_CURVE_COLOR,
  CHART_CURVE_PATH,
  CHART_VIEW_BOX,
  CURVE_DRAW_DURATION_MS,
  DAILY_PACE_I18N_KEYS,
  FOCUS_GOAL_I18N_KEYS,
  KIND_OF_LEARNING_LABELS,
  MARKER_TRAVEL_DURATION_MS,
  SCORE_DENOMINATOR,
  SECTION_NUMBER,
} from '~/pages/anatomyV2/learningProfile/libs/components/AnatomyV2LearningProfile/constants'
import { AnatomyV2LearningProfileStyles as S } from '~/pages/anatomyV2/learningProfile/libs/components/AnatomyV2LearningProfile/styles'
import { calculateTotalScore } from '~/pages/anatomyV2/libs/calculateSectionScore'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import { ALL_SCORED_SECTIONS } from '~/pages/anatomyV2/libs/scoredSections'
import { PageId } from '~/shared/constants/pagesConstants'
import { easeInOutQuad } from '~/shared/helpers/animationFunctions'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { selectAnswers } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'

const StarIcon = ({
  width,
  height,
  color,
}: {
  width: number
  height: number
  color: string
}) => (
  <svg fill="none" height={height} viewBox="0 0 18 16" width={width}>
    <path
      d="M10.0458 0.658666L12.1759 4.38785L16.6364 5.12542C17.6868 5.29917 18.1037 6.49653 17.3637 7.21282L14.2192 10.2553L14.8458 14.4401C14.9935 15.4259 13.9018 16.1658 12.9426 15.7303L8.86924 13.881L4.79591 15.7303C3.83667 16.1658 2.745 15.4259 2.89262 14.4401L3.51924 10.2553L0.374752 7.21282C-0.36591 6.49653 0.0509924 5.29917 1.10211 5.12542L5.56255 4.38785L7.69267 0.658666C8.19447 -0.219555 9.544 -0.219555 10.0458 0.658666Z"
      fill={color}
    />
  </svg>
)

const DailyPaceIcon = () => (
  <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
    <path
      d="M8.00499 2C5.14429 2 2.60609 4.0157 2.03619 6.7812C1.92489 7.3216 2.27659 7.8575 2.81749 7.9688C3.35839 8.08 3.89369 7.7279 4.00499 7.1875C4.38439 5.3461 6.09809 4 8.00499 4C8.55729 4 9.00499 3.5517 9.00499 3C9.00499 2.4483 8.55729 2 8.00499 2ZM16.005 2C15.4527 2 15.005 2.4477 15.005 3C15.005 3.5523 15.4527 4 16.005 4C17.9141 4 19.628 5.3412 20.005 7.1875C20.1155 7.7286 20.6514 8.0792 21.1925 7.9688C21.7336 7.8583 22.0842 7.3224 21.9738 6.7812C21.4075 4.0083 18.8691 2 16.005 2ZM12.005 4C7.03439 4 3.00499 8.0294 3.00499 13C3.00499 15.1106 3.75719 17.0902 4.97379 18.625L3.31749 20.2812C2.92699 20.6718 2.92699 21.3282 3.31749 21.7188C3.70799 22.1093 4.33319 22.1093 4.72379 21.7188L6.37999 20.0312C7.91479 21.2478 9.89439 22 12.005 22C14.1156 22 16.0952 21.2478 17.63 20.0312L19.3175 21.7188C19.708 22.1093 20.3332 22.078 20.7238 21.6875C21.1143 21.297 21.083 20.6718 20.6925 20.2812L19.0362 18.5938C20.2348 17.1407 21.005 15.1106 21.005 13C21.005 8.0294 16.9756 4 12.005 4ZM12.005 8C12.5573 8 13.005 8.4477 13.005 9V13C13.005 13.5523 12.5573 14 12.005 14H8.00499C7.45269 14 7.00499 13.5523 7.00499 13C7.00499 12.4477 7.45269 12 8.00499 12H11.005V9C11.005 8.4477 11.4527 8 12.005 8Z"
      fill="#33C558"
    />
  </svg>
)

const LearningStyleIcon = () => (
  <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
    <path
      d="M6.00001 2.99219C4.343 2.99219 3 4.33529 3 5.99219V7.99219V16.9922C3 19.2012 4.791 20.9922 7.00001 20.9922H17C19.209 20.9922 21 19.2012 21 16.9922V7.99219C21 7.43989 20.552 6.99219 20 6.99219C20 6.99219 17.097 6.99219 15 6.99219C14.245 6.99219 14.002 6.74729 13.406 5.55469C12.503 3.74729 11.745 2.99219 10 2.99219H6.00001ZM6.00001 4.99219H10C10.755 4.99219 10.998 5.23709 11.594 6.42969C11.724 6.69079 11.774 6.77579 11.902 6.99389C8.45602 6.99389 5.985 6.99219 5 6.99219V5.99219C5 5.43989 5.448 4.99219 6.00001 4.99219Z"
      fill="#33C558"
    />
  </svg>
)

const FocusGoalIcon = () => (
  <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
    <path
      d="M11.998 0.523438C7.57868 0.523438 3.99609 4.10603 3.99609 8.52539C3.99609 9.94059 4.37789 11.2883 5.06834 12.4909C5.08092 12.5138 7.23346 15.8963 7.4255 16.5273H16.5706C16.7592 15.8998 18.9049 12.5412 18.8923 12.5618C19.6045 11.3467 20 9.96346 20 8.52539C20 4.10603 16.4174 0.523438 11.998 0.523438ZM11.998 5.09598C12.6291 5.09598 13.1412 5.60811 13.1412 6.23912C13.1412 6.87013 12.6291 7.38225 11.998 7.38225C11.367 7.38225 10.8549 7.89438 10.8549 8.52539C10.8549 9.1564 10.3428 9.66853 9.71177 9.66853C9.08076 9.66853 8.56864 9.1564 8.56864 8.52539C8.56864 6.63121 10.1039 5.09598 11.998 5.09598ZM7.4255 18.8136C7.4255 21.3388 9.47286 23.3862 11.998 23.3862C14.5232 23.3862 16.5706 21.3388 16.5706 18.8136H7.4255Z"
      fill="#33C558"
    />
  </svg>
)

const getStartingPointKey = (score: number) => {
  if (score <= 40) return 'curiousBeginner'
  if (score <= 70) return 'fastLearner'
  return 'readyForMore'
}

const formatLearningStyle = (keys: string[]) => {
  if (!keys.length) return ''

  const [first, ...rest] = keys.map(
    (key) => KIND_OF_LEARNING_LABELS[key] ?? key,
  )
  const capitalizedFirst = first.charAt(0).toUpperCase() + first.slice(1)

  return `${[capitalizedFirst, ...rest].join(', ')}.`
}

const AnatomyV2LearningProfile = ({ pageId, nextPagePath }: TPageProps) => {
  useMatchHeaderBackground()

  const { t } = useTranslation()
  const answers = useSelector(selectAnswers)
  const curvePathRef = useRef<SVGPathElement>(null)
  const [scorePoint, setScorePoint] = useState({ x: 0, y: CHART_BASELINE_Y })

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'Your learning profile',
  })

  const score = calculateTotalScore(ALL_SCORED_SECTIONS, answers)

  const startingPointKey = getStartingPointKey(score)

  useLayoutEffect(() => {
    const curveEl = curvePathRef.current
    if (!curveEl) return undefined

    const totalLength = curveEl.getTotalLength()
    const progress = Math.min(Math.max(score / SCORE_DENOMINATOR, 0), 1)
    const targetLength = totalLength * progress

    setScorePoint(curveEl.getPointAtLength(0))

    let animationFrameId = 0
    let startTimestamp: number | null = null

    const animate = (timestamp: number) => {
      if (startTimestamp === null) startTimestamp = timestamp
      const elapsed = timestamp - startTimestamp
      const travelProgress = Math.min(elapsed / MARKER_TRAVEL_DURATION_MS, 1)
      const length = targetLength * easeInOutQuad(travelProgress)
      setScorePoint(curveEl.getPointAtLength(length))

      if (travelProgress < 1) {
        animationFrameId = requestAnimationFrame(animate)
      }
    }

    const timeoutId = setTimeout(() => {
      animationFrameId = requestAnimationFrame(animate)
    }, CURVE_DRAW_DURATION_MS)

    return () => {
      clearTimeout(timeoutId)
      cancelAnimationFrame(animationFrameId)
    }
  }, [score])

  const dailyGoalAnswer = String(answers?.[PageId.DAILY_GOAL_ANATOMY_1] ?? '')
  const dailyPaceI18nKey = DAILY_PACE_I18N_KEYS[dailyGoalAnswer]

  const kindOfLearningAnswer = answers?.[PageId.KIND_OF_LEARNING_ANATOMY_V2_1]
  const learningStyleValue = formatLearningStyle(
    Array.isArray(kindOfLearningAnswer) ? kindOfLearningAnswer : [],
  )

  const doomscrollingAnswer = String(
    answers?.[PageId.DOOMSCROLLING_TIME_ANATOMY_V2_1] ?? '',
  )
  const focusGoalI18nKey = FOCUS_GOAL_I18N_KEYS[doomscrollingAnswer]

  return (
    <S.Root>
      <S.ContentWrapper>
        <S.ScrollContent>
          <SectionProgress currentSection={SECTION_NUMBER} pageId={pageId} />

          <S.Title>
            <Trans ns="anatomyV2" i18nKey="onboarding.learningProfile.title" />
          </S.Title>

          <S.ChartWrapper>
            <S.ChartSvg
              viewBox={CHART_VIEW_BOX}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient
                  id="learningProfileChartGradient"
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={CHART_CURVE_COLOR}
                    stopOpacity="0.35"
                  />
                  <stop
                    offset="100%"
                    stopColor={CHART_CURVE_COLOR}
                    stopOpacity="0"
                  />
                </linearGradient>

                <radialGradient
                  id="learningProfileMarkerGlow"
                  cx="0"
                  cy="0"
                  gradientTransform="translate(13.9172 13.9172) rotate(90) scale(13.9172)"
                  gradientUnits="userSpaceOnUse"
                  r="1"
                >
                  <stop stopColor="white" stopOpacity="0" />
                  <stop offset="0.659727" stopColor="white" stopOpacity="0" />
                  <stop offset="1" stopColor="white" />
                </radialGradient>
              </defs>

              <path
                d={`${CHART_CURVE_PATH} L343 ${CHART_BASELINE_Y} L0 ${CHART_BASELINE_Y} Z`}
                fill="url(#learningProfileChartGradient)"
              />

              <S.AnimatedCurve
                ref={curvePathRef}
                d={CHART_CURVE_PATH}
                fill="none"
                pathLength="1"
                stroke={CHART_CURVE_COLOR}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3.5"
              />

              <S.ScoreMarkerGroup
                transform={`translate(${scorePoint.x - 14}, ${
                  scorePoint.y - 14
                })`}
              >
                <circle
                  cx="13.9172"
                  cy="13.9172"
                  fill="url(#learningProfileMarkerGlow)"
                  opacity="0.5"
                  r="13.9172"
                  transform="matrix(-1 0 0 1 27.8359 0)"
                />
                <circle
                  cx="3.66242"
                  cy="3.66242"
                  fill="white"
                  r="2.63695"
                  stroke="#6E68EE"
                  strokeWidth="2.05096"
                  transform="matrix(-1 0 0 1 17.582 10.2539)"
                />
              </S.ScoreMarkerGroup>
            </S.ChartSvg>

            <S.ScoreCard>
              <S.ScoreStar>
                <StarIcon color="#6E68EE" height={16} width={18} />
              </S.ScoreStar>
              <S.ScoreRow>
                <S.ScoreNumber>{score}</S.ScoreNumber>
                <S.ScoreDenominator>/{SCORE_DENOMINATOR}</S.ScoreDenominator>
              </S.ScoreRow>
              <S.ScoreLabel>
                <Trans
                  ns="anatomyV2"
                  i18nKey="onboarding.learningProfile.quizResult"
                />
              </S.ScoreLabel>
            </S.ScoreCard>
          </S.ChartWrapper>

          <S.Grid>
            <S.InfoCard>
              <S.IconBadge>
                <StarIcon color="#33C558" height={24} width={24} />
              </S.IconBadge>
              <S.InfoLabel>
                <Trans
                  ns="anatomyV2"
                  i18nKey="onboarding.learningProfile.startingPointLabel"
                />
              </S.InfoLabel>
              <S.InfoValue>
                <Trans
                  ns="anatomyV2"
                  i18nKey={`onboarding.learningProfile.startingPoint.${startingPointKey}`}
                />
              </S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.IconBadge>
                <DailyPaceIcon />
              </S.IconBadge>
              <S.InfoLabel>
                <Trans
                  ns="anatomyV2"
                  i18nKey="onboarding.learningProfile.dailyPaceLabel"
                />
              </S.InfoLabel>
              <S.InfoValue>
                {dailyPaceI18nKey ? t(dailyPaceI18nKey) : ''}
              </S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.IconBadge>
                <LearningStyleIcon />
              </S.IconBadge>
              <S.InfoLabel>
                <Trans
                  ns="anatomyV2"
                  i18nKey="onboarding.learningProfile.learningStyleLabel"
                />
              </S.InfoLabel>
              <S.InfoValue>{learningStyleValue}</S.InfoValue>
            </S.InfoCard>

            <S.InfoCard>
              <S.IconBadge>
                <FocusGoalIcon />
              </S.IconBadge>
              <S.InfoLabel>
                <Trans
                  ns="anatomyV2"
                  i18nKey="onboarding.learningProfile.focusGoalLabel"
                />
              </S.InfoLabel>
              <S.InfoValue>
                {focusGoalI18nKey && (
                  <Trans ns="anatomyV2" i18nKey={focusGoalI18nKey} />
                )}
              </S.InfoValue>
            </S.InfoCard>
          </S.Grid>
        </S.ScrollContent>

        <S.ButtonWrapper>
          <S.StyledButton
            type="button"
            onClick={() => handleContinue('continue')}
          >
            <Trans i18nKey="actions.continue" />
          </S.StyledButton>
        </S.ButtonWrapper>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default AnatomyV2LearningProfile
